import React, { Suspense, lazy, useEffect } from "react";
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
  createTheme,
  responsiveFontSizes,
} from "@mui/material";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { ParallaxProvider } from "react-scroll-parallax";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { theme, lightPalette } from "./theme";
import GlobalStyles from "./GlobalStyles";
import ColorSchemeStyles from "./design/ColorSchemeStyles";
import CriticalStyles from "./shared/components/CriticalStyles";
import Pace from "./shared/components/Pace";
import { PageLoader } from "./shared/components/ui/Loading";
import { tokens } from "./design/theme.next";
import { RouteErrorBoundary } from "./shared/errors/ErrorBoundary";
// Removed web vitals import to fix error
import { Provider, useSelector } from "react-redux";
import store from "./store/store";
import jwt_decode from "jwt-decode";
import { recordTouch } from "./shared/utils/attribution";
import { identify } from "./shared/utils/analytics";
import setAuthToken from "./utils/setAuthToken";
import { FIRM_PATHS } from "./shared/utils/signedIn";
import {

  setCurrentUser,
} from "./store/features/login/login-slice";

const LoggedInComponent = lazy(() => import("./logged_in/components/Main"));
const ConsentBanner = lazy(() => import("./shared/components/insights/ConsentBanner"));
const LoggedOutComponent = lazy(() => import("./logged_out/components/Main"));
const AuthComponent = lazy(() => import("./logged_out/components/auth/Main"));
const JoinUniversity = lazy(() => import("./logged_out/components/auth/JoinUniversity"));
const VerifyEmail = lazy(() => import("./logged_out/components/auth/VerifyEmail"));
const WaitlistComponent = lazy(() =>
  import("./logged_out/components/waitlist/Waitlist")
);

// Check for token to keep user logged in
if (localStorage.jwtToken) {
  try {
    // Set auth token header auth
    const token = localStorage.jwtToken;
    setAuthToken(token);
    // Decode token and get user info and exp
    const decoded = jwt_decode(token);
    // Set user and isAuthenticated
    console.log("user already logged in on start");
    store.dispatch(setCurrentUser(decoded));
    identify(decoded.id);
    // store.dispatch(updateLoginState(true));
    // //store.dispatch(setUserID(decoded))
    // // Check for expired token
    // const currentTime = Date.now() / 1000; // to get in milliseconds
    // if (decoded.exp < currentTime) {
    //   // Logout user
    //   //store.dispatch(logoutUser());
    //   // Redirect to login
    //   window.location.href = "./login";
    // }
  } catch (error) {
    console.error("Invalid JWT token found in localStorage:", error);
    // Clear the invalid token
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("email");
  }
}

// Signed in, a firm's page opens inside the app; signed out, it's the public guide.
function FirmLayout() {
  const signedIn = useSelector((state) => Boolean(state.login?.isAuthenticated));
  return signedIn ? <LoggedInComponent /> : <LoggedOutComponent />;
}

function App() {
  useEffect(() => {
    localStorage.setItem("mode", "company");
    recordTouch();
    
    // Core Web Vitals monitoring removed to fix error
    
  }, []);

  // The site shell is light. The logged-in app picks light or dark itself
  // (Settings → Appearance), see hooks/useThemePreference.
  const currentTheme = responsiveFontSizes(
    createTheme({ ...theme, palette: lightPalette })
  );

  return (
    <Provider store={store}>
      <ParallaxProvider>
        <BrowserRouter>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={currentTheme}>
              <CssBaseline />
              <CriticalStyles />
              <GlobalStyles />
              <ColorSchemeStyles />
              <Pace color={tokens.a4.color.violet} />
              
              <RouteErrorBoundary scope="app" fullPage>
              <Suspense fallback={<PageLoader />}>
                <Switch>
                  <Route path="/c">
                    <LoggedInComponent />
                  </Route>
                  <Route path="/auth">
                    <AuthComponent />
                  </Route>
                  <Route path="/verify-email">
                    <VerifyEmail />
                  </Route>
                  <Route path="/join/:code">
                    <JoinUniversity />
                  </Route>
                  <Route path="/waitlist">
                    <WaitlistComponent />
                  </Route>
                  <Route exact path={FIRM_PATHS}>
                    <FirmLayout />
                  </Route>
                  <Route>
                    <LoggedOutComponent />
                  </Route>
                </Switch>
              </Suspense>
              </RouteErrorBoundary>
              
              <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable={false}
                pauseOnHover
                theme="light"
                icon={false}
              />
              <Suspense fallback={null}>
                <ConsentBanner />
              </Suspense>
            </ThemeProvider>
          </StyledEngineProvider>
        </BrowserRouter>
      </ParallaxProvider>
    </Provider>
  );
}

export default App;
